import React, { useEffect, useState } from "react";
import * as Checkbox from "@radix-ui/react-checkbox";
import { Flex, Text } from "@radix-ui/themes";

interface Option {
  answerId: number;
  value: string;
  isAnswer: boolean;
  isRightAnswer?: boolean;
}

interface MultipleChoiceQuestionProps {
  question: string;
  options: Option[];
  updateAnswers: Function;
  questionId: string;
  surveyState: Object;
  isMandatory: boolean;
  index: number;
  onValidate?: (index: number, isValid: boolean) => void;
  assessmentType?: string;

}

const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  question,
  options,
  updateAnswers,
  questionId,
  isMandatory,
  surveyState,
  index,
  onValidate,
  assessmentType

}) => {
  const [validationMessage, setValidationMessage] = useState<string | null>(null);


  useEffect(() => {
    if (typeof onValidate === "function") {
      if (isMandatory) {
        const hasSelected = options.some((o) => o.isAnswer === true);
        onValidate(index, hasSelected);
      } else {
        // non-mandatory always valid
        onValidate(index, true);
      }
    }
    if (assessmentType === "quiz") {
      const allRightSelected = options.every((opt) =>
        opt.isRightAnswer ? opt.isAnswer : !opt.isAnswer
      );
      const allRequiredSelected = options
        .filter((opt) => opt.isRightAnswer)
        .every((opt) => opt.isAnswer);

      if (allRightSelected && allRequiredSelected && options.some((o) => o.isAnswer)) {
        setValidationMessage("");
      } else if (options.some((o) => o.isAnswer)) {
        setValidationMessage("");
      } else {
        setValidationMessage(null);
      }
    }
  }, [options, isMandatory, index, onValidate]);

  return (
    <div className="flex flex-col bg-white p-[1%] gap-2">
      <h2 className="block mb-2 text-normal font-bold text-app-gray-medium">
        {question} {isMandatory && <span className="text-red-600">*</span>}
      </h2>
      {options?.map((option) => (
        <div
          key={option.answerId}
          className={
            option.isAnswer
              ? "p-[1%] border rounded-md shadow-sm border-purple-600"
              : "p-[1%] border rounded-md shadow-sm"
          }
        >
          <label className="flex space-x-2 cursor-pointer">
            <input
              type="checkbox"
              key={option.answerId}
              checked={option.isAnswer}
              onChange={() => updateAnswers(option, index)}
              className="w-4 h-4 my-auto border rounded-md border-purple-600 accent-purple-600 shadow-inner cursor-pointer focus:ring-transparent focus:ring-purple-600"
            />
            <span className="text-base text-gray-700">{option.value}</span>
          </label>
        </div>
      ))}
      {validationMessage && assessmentType === "quiz" && (
        <span
          className={`mt-2 text-sm ${validationMessage === "Correct Answer"
            ? "text-green-600"
            : "text-red-600"
            }`}
        >
          {validationMessage}
        </span>
      )}
    </div>
  );
};

export default MultipleChoiceQuestion;
